import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import format from 'string-template';

import FirebaseContext from '../../../../context/FirebaseContext';
import useSessionStore from '../../../../hooks/useSessionStore';
import useStorage, { StorageProcessState } from '../../../../hooks/useStorage';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import useBeforeUnloadGuard from '../../../../hooks/useBeforeUnloadGuard';
import useToast from '../../../hooks/useToast';
import WebflowDeploy from '../../../Model/WebflowDeploy';
import {
  buildDeployRequest,
  buildStoragePath,
  buildUploadMetadata,
  deployWebflowSite,
  effectiveDeployTarget,
  hasDeployTarget,
  inspectWebflowUpload,
  isWithinUploadLimit,
  isZipFile,
  MAX_UPLOAD_MB,
} from '../../../utils/webflowDeployer';
import texts from '../texts.json';

/**
 * Where a picked file is, from upload to deploy.
 *
 * FOLLOWING is a deploy whose request died without saying the deploy was never started: the
 * endpoint may still be finishing it, so the webflowDeploys document is the only account left.
 */
const ItemPhase = {
  UPLOADING: 'UPLOADING',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  INSPECTING: 'INSPECTING',
  INSPECT_FAILED: 'INSPECT_FAILED',
  READY: 'READY',
  DEPLOYING: 'DEPLOYING',
  DEPLOYED: 'DEPLOYED',
  FOLLOWING: 'FOLLOWING',
};

/** Phases where something is in flight for the file, so it cannot be removed. */
const BUSY_PHASES = [ItemPhase.UPLOADING, ItemPhase.INSPECTING, ItemPhase.DEPLOYING];

/** Phases from the deploy request onwards, where the run document is what the file shows. */
const DEPLOY_PHASES = [ItemPhase.DEPLOYING, ItemPhase.DEPLOYED, ItemPhase.FOLLOWING];

/**
 * Phases whose work lives in this tab, so leaving the page would lose it. A deploy is not one: it
 * runs server-side and shows up in history either way.
 */
const UNSAVED_PHASES = [ItemPhase.UPLOADING, ItemPhase.INSPECTING];

/** Phases where the upload exists but no deploy has claimed it, so removing it deletes it. */
const ABANDONABLE_PHASES = [ItemPhase.READY, ItemPhase.INSPECT_FAILED];

/**
 * Whether a file can be deployed as it stands: inspected, valid, and with a target picked.
 * @param {Object} item - A queue item.
 * @param {Array} sites - The Netlify sites, which the pre-selected target is derived from.
 * @returns {boolean}
 */
const isItemReady = (item, sites) => {
  if (item.phase !== ItemPhase.READY || !item.inspection?.validation?.isValid) {
    return false;
  }
  const { selectedSiteId, newSiteName } = effectiveDeployTarget(item, sites);
  return hasDeployTarget(selectedSiteId, newSiteName);
};

/**
 * The page's upload queue: each picked ZIP is uploaded, inspected, and then deployed on
 * request. One item per file, keyed by its reserved runId, which names the upload, the
 * deploy request and the deploy document alike.
 *
 * An item stores its target only once the user picks or edits it (null until then, and then
 * both fields at once); before that the pre-selection is derived from the inspection and the
 * sites at render, so sites that load after the inspection still pre-select their name match.
 * See effectiveDeployTarget.
 *
 * @param {Array} sites - The Netlify sites, used to pre-select a site with the suggested name.
 * @returns {Object} The queue and its actions.
 */
const useDeployQueue = (sites) => {
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { userId } = useSessionStore();
  const { uploadFile, deleteAttachment } = useStorage();
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const [items, setItems] = useState([]);
  // Read by the actions, so a click acts on the item as it is now rather than as it rendered.
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  // Guards re-entry into a deploy within a single tick, which state cannot do.
  const deployingIdsRef = useRef(new Set());

  /*
    Every async step lands through here: a no-op once the page is gone or the item has been
    removed, so a late upload or inspection cannot resurrect a row.
  */
  const updateItem = useCallback((runId, changes) => {
    if (!isComponentMountedRef.current) {
      return;
    }
    setItems((current) => current.map((item) => (
      item.runId === runId ? { ...item, ...changes } : item
    )));
  }, [isComponentMountedRef]);

  const inspect = useCallback(async (runId, storagePath) => {
    /*
      The page was left while the upload ran: nobody is there to deploy the file, so it is not
      inspected either. The upload it leaves behind is deleted after 7 days by the bucket's
      lifecycle rule on webflowDeploys/ (system2-server storage.lifecycle.json).
    */
    if (!isComponentMountedRef.current) {
      return;
    }
    updateItem(runId, { phase: ItemPhase.INSPECTING, error: '' });
    try {
      const inspection = await inspectWebflowUpload(storagePath, remote);
      updateItem(runId, { phase: ItemPhase.READY, inspection });
    } catch (error) {
      updateItem(runId, { phase: ItemPhase.INSPECT_FAILED, error: error.message || texts.inspectFailed });
    }
  }, [
    remote,
    updateItem,
    isComponentMountedRef,
  ]);

  const upload = useCallback(async (item, file) => {
    const { runId, storagePath } = item;
    let lastProgress = 0;
    let result;
    try {
      result = await uploadFile({
        file,
        storagePath,
        metadata: buildUploadMetadata(file.name),
        onProgress: (percent) => {
          // Whole percents only: storage reports every chunk, and each update re-renders the row.
          const uploadProgress = Math.round(percent);
          if (uploadProgress !== lastProgress) {
            lastProgress = uploadProgress;
            updateItem(runId, { uploadProgress });
          }
        },
      });
    } catch (error) {
      // Upload task failures resolve as FAILED; this catches put() itself throwing.
      result = { state: StorageProcessState.FAILED };
    }

    if (result.state !== StorageProcessState.SUCCESS) {
      updateItem(runId, { phase: ItemPhase.UPLOAD_FAILED, error: texts.uploadFailed });
      return;
    }

    await inspect(runId, storagePath);
  }, [
    uploadFile,
    updateItem,
    inspect,
  ]);

  const addFiles = useCallback((files) => {
    const allZips = files.filter(isZipFile);
    const zips = allZips.filter(isWithinUploadLimit);
    const skippedNonZip = files.length - allZips.length;
    const skippedTooLarge = allZips.length - zips.length;
    if (skippedNonZip > 0) {
      showToast(format(texts.skippedNonZip, { count: skippedNonZip }), { warning: true });
    }
    if (skippedTooLarge > 0) {
      showToast(format(texts.skippedTooLarge, { count: skippedTooLarge, limit: MAX_UPLOAD_MB }), { warning: true });
    }

    const newItems = zips.map((file) => {
      const runId = WebflowDeploy.reserveRunId();
      return {
        file,
        item: {
          runId,
          fileName: file.name,
          storagePath: buildStoragePath(userId, runId),
          phase: ItemPhase.UPLOADING,
          uploadProgress: 0,
          inspection: null,
          selectedSiteId: null,
          newSiteName: null,
          error: '',
          deployError: '',
          result: null,
        },
      };
    });

    setItems((current) => [...current, ...newItems.map(({ item }) => item)]);
    newItems.forEach(({ item, file }) => upload(item, file));
  }, [
    userId,
    upload,
    showToast,
  ]);

  const removeItem = useCallback((runId) => {
    const item = itemsRef.current.find((entry) => entry.runId === runId);
    if (!item || BUSY_PHASES.includes(item.phase)) {
      return;
    }
    setItems((current) => current.filter((entry) => entry.runId !== runId));
    /*
      An upload no deploy has claimed would otherwise sit in storage for good. One a deploy has
      claimed belongs to the deploy, and removing the row only hides it — history still has it.
    */
    if (ABANDONABLE_PHASES.includes(item.phase)) {
      deleteAttachment(item.storagePath, { ignoreNotFound: true });
    }
  }, [deleteAttachment]);

  /*
    The first edit to either field stores the whole target as it stands, so a site list that
    loads afterwards cannot move the selection out from under a name the user typed.
  */
  const setTarget = useCallback((runId, { selectedSiteId, newSiteName }) => {
    setItems((current) => current.map((item) => {
      if (item.runId !== runId) {
        return item;
      }
      const target = effectiveDeployTarget(item, sites);
      return {
        ...item,
        deployError: '',
        selectedSiteId: selectedSiteId ?? target.selectedSiteId,
        newSiteName: newSiteName ?? target.newSiteName,
      };
    }));
  }, [sites]);

  const deploy = useCallback(async (runId) => {
    const item = itemsRef.current.find((entry) => entry.runId === runId);
    if (!item || !isItemReady(item, sites) || deployingIdsRef.current.has(runId)) {
      return;
    }
    deployingIdsRef.current.add(runId);
    updateItem(runId, { phase: ItemPhase.DEPLOYING, deployError: '' });

    try {
      const result = await deployWebflowSite(
        buildDeployRequest({ ...item, ...effectiveDeployTarget(item, sites) }),
        remote,
      );
      updateItem(runId, { phase: ItemPhase.DEPLOYED, result });
    } catch (error) {
      const message = error.message || texts.deployFailed;
      if (error.isRunRejected) {
        // Never started, so the same runId is still unused and the file can be deployed again.
        updateItem(runId, { phase: ItemPhase.READY, deployError: message });
      } else {
        updateItem(runId, { phase: ItemPhase.FOLLOWING, deployError: message });
      }
    }
    deployingIdsRef.current.delete(runId);
  }, [
    sites,
    remote,
    updateItem,
  ]);

  // A refresh or tab close mid-upload or mid-inspection loses the file, so the browser asks first.
  useBeforeUnloadGuard(items.some(({ phase }) => UNSAVED_PHASES.includes(phase)));

  const readyIds = useMemo(() => items
    .filter((item) => isItemReady(item, sites))
    .map(({ runId }) => runId), [
    items,
    sites,
  ]);

  const deployAllReady = useCallback(() => {
    readyIds.forEach((runId) => deploy(runId));
  }, [
    readyIds,
    deploy,
  ]);

  return {
    items,
    readyCount: readyIds.length,
    addFiles,
    removeItem,
    setTarget,
    deploy,
    deployAllReady,
  };
};

export default useDeployQueue;
export {
  ItemPhase,
  BUSY_PHASES,
  DEPLOY_PHASES,
  isItemReady,
};
